<script setup>
import { ref, computed, onMounted } from 'vue';
import PlanAPI from 'dashboard/api/plan';

const loading = ref(true);
const data = ref(null);
const errorMessage = ref(null);

const plan = computed(() => data.value?.plan);
const balance = computed(() => data.value?.balance);

const usagePercent = computed(() => balance.value?.usage_percent || 0);
const usageColor = computed(() => {
  if (usagePercent.value >= 100) return 'bg-n-ruby-9';
  if (usagePercent.value >= 80) return 'bg-n-amber-9';
  return 'bg-n-brand';
});

const fetchPlan = async () => {
  loading.value = true;
  errorMessage.value = null;
  try {
    const { data: payload } = await PlanAPI.getPlan();
    data.value = payload;
  } catch (e) {
    errorMessage.value =
      e?.response?.data?.error || 'Erro ao carregar plano. Tente recarregar.';
  } finally {
    loading.value = false;
  }
};

const formatDate = iso =>
  iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';
const formatBrl = num =>
  num != null ? `R$ ${num.toFixed(2).replace('.', ',')}` : '—';

// ============================================================
// Upgrade modal
// ============================================================

const TIERS = [
  {
    key: 'starter',
    name: 'Starter',
    price: 897,
    highlight: false,
    bullets: [
      '3.000 interacoes IA/mes',
      '2 caixas de entrada',
      '5 usuarios',
      '1 Humano Digital',
      '50MB de RAG',
      'WhatsApp Meta (2 numeros)',
    ],
  },
  {
    key: 'growth',
    name: 'Growth',
    price: 1697,
    highlight: true,
    bullets: [
      '8.000 interacoes IA/mes',
      '5 caixas de entrada',
      '10 usuarios',
      '2 Humanos Digitais',
      '200MB de RAG',
      'WhatsApp Meta (5 numeros)',
      'Suporte prioritario',
    ],
  },
  {
    key: 'scale',
    name: 'Scale',
    price: 3197,
    highlight: false,
    bullets: [
      '20.000 interacoes IA/mes',
      '10 caixas de entrada',
      '20 usuarios',
      '5 Humanos Digitais',
      '500MB de RAG',
      'WhatsApp Meta (10 numeros)',
      'API + MCP access',
    ],
  },
];

const showUpgradeModal = ref(false);
const upgradeForm = ref({
  tier: 'growth',
  billing_type: 'BOLETO',
  cpf_cnpj: '',
});
const upgrading = ref(false);
const upgradeError = ref(null);
const upgradeSuccess = ref(null);

const sanitizedCpf = computed(() =>
  upgradeForm.value.cpf_cnpj.replace(/\D/g, '')
);
const cpfValid = computed(() => [11, 14].includes(sanitizedCpf.value.length));

const canSubmitUpgrade = computed(
  () => !!upgradeForm.value.tier && cpfValid.value && !upgrading.value
);

const formatCpfInput = e => {
  let val = e.target.value.replace(/\D/g, '').slice(0, 14);
  if (val.length <= 11) {
    val = val
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1-$2');
  } else {
    val = val
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }
  upgradeForm.value.cpf_cnpj = val;
};

const openUpgradeModal = () => {
  upgradeError.value = null;
  upgradeSuccess.value = null;
  // Pre-seleciona o proximo tier acima do atual
  const currentTier = plan.value?.tier || 'essencial';
  const nextMap = {
    essencial: 'starter',
    starter: 'growth',
    growth: 'scale',
    scale: 'scale',
  };
  upgradeForm.value.tier = nextMap[currentTier] || 'growth';
  upgradeForm.value.cpf_cnpj = '';
  upgradeForm.value.billing_type = 'BOLETO';
  upgradeForm.value.cpf_cnpj = '';
  showUpgradeModal.value = true;
};

let pollInterval = null;

const stopPolling = () => {
  if (pollInterval) {
    clearInterval(pollInterval);
    pollInterval = null;
  }
};

const startPaymentPolling = () => {
  stopPolling();
  const pendingTier = upgradeForm.value.tier;
  pollInterval = setInterval(async () => {
    try {
      const { data: payload } = await PlanAPI.getPlan();
      if (
        payload.plan?.status === 'active' &&
        payload.plan?.tier === pendingTier
      ) {
        stopPolling();
        data.value = payload;
        upgradeSuccess.value = null;
        showUpgradeModal.value = false;
      }
    } catch {
      // silencioso
    }
  }, 5000);
};

const closeUpgradeModal = () => {
  if (upgrading.value) return;
  stopPolling();
  showUpgradeModal.value = false;
  fetchPlan();
};

const submitUpgrade = async () => {
  if (!canSubmitUpgrade.value) return;
  upgrading.value = true;
  upgradeError.value = null;
  try {
    const { data: result } = await PlanAPI.upgrade({
      tier: upgradeForm.value.tier,
      billing_type: upgradeForm.value.billing_type,
      cpf_cnpj: sanitizedCpf.value,
    });
    upgradeSuccess.value = result;
    startPaymentPolling();
  } catch (e) {
    upgradeError.value =
      e?.response?.data?.error ||
      'Erro ao processar upgrade. Confira os dados e tente de novo.';
  } finally {
    upgrading.value = false;
  }
};

onMounted(fetchPlan);
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="w-full">
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-n-slate-12 mb-1">Plano e Cobranca</h1>
      <p class="text-sm text-n-slate-11">
        Gerencie seu plano, consumo de IA e cobranca.
      </p>
    </div>

    <div v-if="loading" class="text-center py-16 text-n-slate-10">
      Carregando...
    </div>

    <div
      v-else-if="errorMessage"
      class="text-sm text-n-ruby-11 bg-n-ruby-3 border border-n-ruby-6 rounded-xl px-4 py-3"
    >
      {{ errorMessage }}
    </div>

    <div v-else-if="!plan" class="text-center py-16 text-n-slate-11">
      <p class="mb-4">Sua conta nao possui plano configurado.</p>
      <p class="text-xs text-n-slate-10">Entre em contato com o suporte.</p>
    </div>

    <div v-else class="space-y-4">
      <!-- Plan card -->
      <div class="bg-n-solid-1 border border-n-weak rounded-2xl p-6">
        <div class="flex items-start justify-between mb-4">
          <div>
            <div class="text-xs uppercase tracking-wider text-n-slate-10 mb-1">
              Seu plano
            </div>
            <h2 class="text-2xl font-bold text-n-slate-12">
              {{ plan.tier_label }}
            </h2>
            <div class="text-sm text-n-slate-11 mt-1">
              {{ formatBrl(plan.monthly_price_brl) }}/mes ·
              <span
                class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                :class="{
                  'bg-n-teal-3 text-n-teal-11': plan.status === 'active',
                  'bg-n-amber-3 text-n-amber-11': plan.status === 'trial',
                  'bg-n-ruby-3 text-n-ruby-11': [
                    'past_due',
                    'canceled',
                  ].includes(plan.status),
                  'bg-n-alpha-2 text-n-slate-11': plan.status === 'paused',
                }"
              >
                {{ plan.status }}
              </span>
            </div>
          </div>
          <button
            class="px-4 py-2 rounded-lg font-semibold text-white bg-n-brand hover:opacity-90"
            @click="openUpgradeModal"
          >
            Fazer upgrade →
          </button>
        </div>

        <div
          v-if="plan.trial"
          class="mb-4 p-3 rounded-lg bg-n-amber-3 border border-n-amber-6"
        >
          <div class="flex items-baseline justify-between">
            <div>
              <div class="text-xs font-semibold text-n-amber-11 uppercase">
                Trial ativo
              </div>
              <div class="text-sm text-n-slate-12 mt-0.5">
                Termina em
                <strong>{{ formatDate(plan.trial.ends_at) }}</strong> ({{
                  plan.trial.days_remaining
                }}
                dias restantes)
              </div>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
          <div class="bg-n-alpha-1 rounded-lg px-3 py-2">
            <div class="text-xs text-n-slate-10">Interacoes/mes</div>
            <div class="font-semibold text-n-slate-12">
              {{ plan.included.ai_interactions.toLocaleString('pt-BR') }}
            </div>
          </div>
          <div class="bg-n-alpha-1 rounded-lg px-3 py-2">
            <div class="text-xs text-n-slate-10">Caixas</div>
            <div class="font-semibold text-n-slate-12">
              {{ plan.included.inboxes < 0 ? '∞' : plan.included.inboxes }}
            </div>
          </div>
          <div class="bg-n-alpha-1 rounded-lg px-3 py-2">
            <div class="text-xs text-n-slate-10">Usuarios</div>
            <div class="font-semibold text-n-slate-12">
              {{ plan.included.users < 0 ? '∞' : plan.included.users }}
            </div>
          </div>
          <div class="bg-n-alpha-1 rounded-lg px-3 py-2">
            <div class="text-xs text-n-slate-10">HDs</div>
            <div class="font-semibold text-n-slate-12">
              {{
                plan.included.digital_humans < 0
                  ? '∞'
                  : plan.included.digital_humans
              }}
            </div>
          </div>
          <div class="bg-n-alpha-1 rounded-lg px-3 py-2">
            <div class="text-xs text-n-slate-10">RAG</div>
            <div class="font-semibold text-n-slate-12">
              {{
                plan.included.rag_storage_mb < 0
                  ? '∞'
                  : plan.included.rag_storage_mb + 'MB'
              }}
            </div>
          </div>
        </div>
      </div>

      <!-- Balance card -->
      <div
        v-if="balance"
        class="bg-n-solid-1 border border-n-weak rounded-2xl p-6"
      >
        <div class="flex items-baseline justify-between mb-4">
          <div>
            <div class="text-xs uppercase tracking-wider text-n-slate-10 mb-1">
              Consumo do ciclo atual
            </div>
            <h2 class="text-xl font-bold text-n-slate-12">
              {{ balance.consumed_interactions.toLocaleString('pt-BR') }}
              <span class="text-n-slate-10 text-base font-normal">
                / {{ balance.included_interactions.toLocaleString('pt-BR') }}
              </span>
            </h2>
            <div class="text-xs text-n-slate-10 mt-1">
              Periodo: {{ formatDate(balance.period_start) }} →
              {{ formatDate(balance.period_end) }}
            </div>
          </div>
          <div class="text-right">
            <div class="text-3xl font-bold text-n-slate-12">
              {{ usagePercent }}%
            </div>
            <div class="text-xs text-n-slate-10">usado</div>
          </div>
        </div>

        <div class="w-full h-2 bg-n-alpha-2 rounded-full overflow-hidden">
          <div
            class="h-full transition-all duration-300"
            :class="usageColor"
            :style="{ width: Math.min(usagePercent, 100) + '%' }"
          />
        </div>

        <div
          v-if="balance.overage_interactions > 0"
          class="mt-3 text-sm text-n-amber-11"
        >
          ⚠️ Overage no ciclo: {{ balance.overage_interactions }} interacoes ({{
            formatBrl(balance.overage_cost_brl)
          }})
        </div>
      </div>
    </div>

    <!-- ============================================================ -->
    <!-- Upgrade Modal                                                  -->
    <!-- ============================================================ -->
    <div
      v-if="showUpgradeModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      @click.self="closeUpgradeModal"
    >
      <div
        class="bg-n-background border border-n-weak rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl"
      >
        <!-- Header -->
        <div
          class="sticky top-0 bg-n-background border-b border-n-weak px-6 py-4 flex items-center justify-between"
        >
          <div>
            <h2 class="text-xl font-bold text-n-slate-12">
              {{ upgradeSuccess ? 'Falta pouco!' : 'Escolha seu novo plano' }}
            </h2>
            <p class="text-xs text-n-slate-10 mt-0.5">
              {{
                upgradeSuccess
                  ? 'Efetue o pagamento para ativar o upgrade'
                  : 'Pagamento processado via Asaas · Banco Central'
              }}
            </p>
          </div>
          <button
            class="p-2 rounded-lg hover:bg-n-alpha-2 text-n-slate-10 disabled:opacity-50"
            :disabled="upgrading"
            @click="closeUpgradeModal"
          >
            ✕
          </button>
        </div>

        <!-- SUCCESS state -->
        <div v-if="upgradeSuccess" class="p-6">
          <div class="text-center mb-6">
            <div
              class="size-16 mx-auto rounded-full bg-n-amber-3 text-n-amber-11 flex items-center justify-center text-3xl mb-4"
            >
              →
            </div>
            <h3 class="text-lg font-bold text-n-slate-12 mb-1">
              Upgrade para
              {{
                upgradeSuccess.pending_tier_label ||
                upgradeSuccess.plan?.tier_label ||
                'novo plano'
              }}
            </h3>
            <p class="text-sm text-n-slate-11">
              Para ativar o plano, efetue o pagamento clicando no botao abaixo.
              Seu upgrade sera ativado automaticamente apos a confirmacao.
            </p>
          </div>

          <div
            v-if="upgradeSuccess.payment_link"
            class="bg-n-alpha-1 border border-n-weak rounded-xl p-4 mb-4"
          >
            <a
              :href="upgradeSuccess.payment_link"
              target="_blank"
              rel="noopener noreferrer"
              class="block w-full py-3 rounded-lg font-semibold text-white bg-n-brand hover:opacity-90 text-center"
            >
              Pagar agora no Asaas →
            </a>
            <p class="text-xs text-n-slate-10 mt-3 text-center">
              Link seguro do Asaas. Apos confirmar o pagamento, sua conta sera
              ativada automaticamente via webhook.
            </p>
          </div>

          <button
            class="w-full py-2.5 rounded-lg text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2"
            @click="closeUpgradeModal"
          >
            Fechar
          </button>
        </div>

        <!-- FORM state -->
        <div v-else class="p-6">
          <!-- Tier cards -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            <button
              v-for="tier in TIERS"
              :key="tier.key"
              type="button"
              class="text-left p-4 rounded-xl border-2 transition-all"
              :class="
                upgradeForm.tier === tier.key
                  ? 'border-n-brand bg-n-brand/5 ring-2 ring-n-brand/30'
                  : 'border-n-weak bg-n-solid-1 hover:border-n-brand/50'
              "
              @click="upgradeForm.tier = tier.key"
            >
              <div
                v-if="tier.highlight"
                class="text-[10px] font-semibold uppercase tracking-wider text-n-brand mb-1"
              >
                ★ Mais popular
              </div>
              <div class="text-base font-bold text-n-slate-12">
                {{ tier.name }}
              </div>
              <div class="text-2xl font-bold text-n-slate-12 my-1">
                R$ {{ tier.price.toLocaleString('pt-BR') }}
                <span class="text-xs font-normal text-n-slate-10">/mes</span>
              </div>
              <ul class="space-y-1 mt-2">
                <li
                  v-for="b in tier.bullets"
                  :key="b"
                  class="text-xs text-n-slate-11 flex items-start gap-1.5"
                >
                  <span class="text-n-brand mt-0.5">✓</span>
                  <span>{{ b }}</span>
                </li>
              </ul>
            </button>
          </div>

          <p class="text-center text-xs text-n-slate-10 mb-4">
            Precisa de mais?
            <a
              href="mailto:contato@owmni.io?subject=Plano%20Enterprise%20Owmni"
              class="text-n-brand font-semibold hover:underline"
            >
              Fale conosco para um plano Enterprise personalizado.
            </a>
          </p>

          <!-- CPF input -->
          <div class="mb-4">
            <label
              for="upgrade-cpf"
              class="block text-sm font-medium text-n-slate-12 mb-1.5"
            >
              CPF ou CNPJ *
            </label>
            <input
              id="upgrade-cpf"
              :value="upgradeForm.cpf_cnpj"
              type="text"
              placeholder="000.000.000-00"
              class="w-full px-3 py-2.5 rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-n-brand"
              @input="formatCpfInput"
            />
            <p
              v-if="upgradeForm.cpf_cnpj && !cpfValid"
              class="text-xs text-n-ruby-11 mt-1"
            >
              CPF/CNPJ invalido
            </p>
          </div>

          <p class="text-xs text-n-slate-10">
            Apos confirmar, voce sera redirecionado para a pagina segura do
            Asaas para escolher a forma de pagamento (Boleto, PIX ou Cartao).
          </p>

          <!-- Error -->
          <div
            v-if="upgradeError"
            class="text-sm text-n-ruby-11 bg-n-ruby-3 border border-n-ruby-6 rounded-lg px-3 py-2 mb-4"
          >
            {{ upgradeError }}
          </div>

          <!-- Actions -->
          <div class="flex gap-3">
            <button
              type="button"
              class="flex-1 py-3 rounded-lg text-sm font-medium text-n-slate-11 border border-n-weak hover:bg-n-alpha-2 disabled:opacity-50"
              :disabled="upgrading"
              @click="closeUpgradeModal"
            >
              Cancelar
            </button>
            <button
              type="button"
              :disabled="!canSubmitUpgrade"
              class="flex-1 py-3 rounded-lg font-semibold text-white bg-n-brand hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
              @click="submitUpgrade"
            >
              {{
                upgrading ? 'Processando...' : 'Confirmar e gerar cobranca →'
              }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
